import RenderManager from "../rendering/render-manager";
import Utils from "../utils"
/**
 * Enques live labels.
 * @param {Object} props Parameters.
 * @param {Object} props.data Project data.
 * @param {Object} props.queue Livelabel queue.
 */
function enqueLivelabels({ data, dataQ3, assetsQ3, isAsset, queue, filteredAnimationData = {} }) {
    function loadLabelsV2(referenceLabels) {
        let url, s3Id;

        s3Id = data.s3IdLabel || data.s3Id || Q3.data.s3Id;

        if (Q3.data._id === "new" && Q3.data.starterS3Id)
            s3Id = Q3.data.starterS3Id

        url = `${Q3.s3Url}/${s3Id}/livelabels${Utils.queryParams.has("draft") ? ("_" + Q3.user._id) : ""}.json`;

        queue.addItem(3, {
            uuid: data.s3IdLabel || data.s3Id,
            data: { name: "livelabels", path: url },
            type: "liveLabel"
        }, params => loadAllLivelabels(params, data, filteredAnimationData, referenceLabels));
    }

    if (data.liveLabels.version === "2.1") {
        if (isAsset) {
            deserializeLivelabels({
                assets: assetsQ3,
                labels: data.liveLabels.labels,
                livelabelManager: Q3.labels.live,
                data: dataQ3,
                filteredAnimationData: {}
            });
        } else {
            const assetLabels = "labels" in data.liveLabels
                ? data.liveLabels.labels
                : [];
            delete data.liveLabels.labels;
            loadLabelsV2(assetLabels);
        }
    } else if (data.liveLabels.version === "2.0") loadLabelsV2([]);
    else {
        Object.keys(data.liveLabels).forEach(key => {
            let uuid, url, s3Id;

            if (!data.assets.objects[key]) {
                delete data.liveLabels[uuid];
                return;
            }

            if (typeof data.liveLabels[key] === "string") {
                uuid = data.liveLabels[key];
                s3Id = data.s3Id || Q3.data.s3Id
                url = `${Q3.s3Url}/${s3Id}/live-labels/${uuid}.json`;
            } else {
                uuid = data.liveLabels[key].uuid;
                s3Id = data.liveLabels[key].s3Id;
                url = `${Q3.s3Url}/${s3Id}/live-labels/${uuid}.json`;
            }

            queue.addItem(3, {
                uuid: uuid,
                data: { objectUuid: key, labelUuid: uuid, path: url },
                type: "liveLabel"
            }, params => loadLivelabel(params, data, filteredAnimationData));
        });
    }
}

/**
 * Deserialize livelabels from data.
 * @param {Object} props Parameters.
 * @param {Object} props.assets Assets.
 * @param {Object} props.data Livelabel data.
 * @param {Object} props.livelabelManager Livelabel manager.
 */
function deserializeLivelabels({ assets, labels, livelabelManager, filteredAnimationData, data }) {
    if (!(labels instanceof Array)) {
        labels = [];
        console.warn("This is an invalid type, removing labels.");
    }

    labels.forEach(labels => {
        const object = assets.objects[labels.object_uuid];

        if (object)
            deserializeLabels({ object, data, labelData: labels, livelabelManager, filteredAnimationData });
    });
}

export { enqueLivelabels, deserializeLivelabels };

async function loadAllLivelabels({ data, loaders, assets, item, livelabelManager }, dataQ3, filteredAnimationData, referenceLabels = []) {
    function deserializeLabels(...labels) {
        const flatLabels = {};

        labels
            .forEach(objectLabels => {
                if (objectLabels instanceof Array) {
                    objectLabels
                        .forEach(({ object_uuid: uuidObject, label: labels }) => {
                            const { meshLabel, regionLabels } = flatLabels[uuidObject] = flatLabels[uuidObject] || {
                                meshLabel: {},
                                regionLabels: {}
                            };

                            labels.forEach((label, index) => {

                                function processMeshLabel() {
                                    Object.assign(meshLabel, label);
                                    meshLabel.index = 0;
                                }

                                function processRegionLabel() {
                                    if (!(label.uuid in regionLabels)) regionLabels[label.uuid] = {};
                                    Object.assign(regionLabels[label.uuid], label);
                                }

                                if (index === 0) processMeshLabel();
                                else processRegionLabel();
                            });
                        });
                }
            });

        data.liveLabels = Object
            .keys(flatLabels)
            .reduce((accum, objectUuid) => {
                const { meshLabel, regionLabels } = flatLabels[objectUuid];
                const finalizedLabels = Object
                    .values(regionLabels)
                    .reduce((accum, label, index) => {
                        accum.push(label);
                        label.index = index + 1;

                        return accum;
                    }, [meshLabel]);

                accum.push({
                    object_uuid: objectUuid,
                    label: finalizedLabels
                })

                return accum;
            }, []);

        deserializeLivelabels({
            assets,
            livelabelManager,
            labels: data.liveLabels,
            filteredAnimationData,
            data: dataQ3
        });
    }

    try {
        const tourLabels = await new Promise(function (resolve, reject) {
            function success(labels) { resolve(JSON.parse(labels)); }

            loaders.file.load(item.data.path, success, undefined, reject);
        });

        deserializeLabels(tourLabels, referenceLabels);
    } catch (ex) {
        console.error(ex);

        deserializeLabels(referenceLabels);
    }
}

/**
 * Load livelabels from file.
 * @param {Object} props Parameters.
 * @param {Object} props.loaders Loaders.
 * @param {Object} props.assets Assets.
 * @param {Object} props.item Livelabel data.
 * @param {Object} props.livelabelManager Livelabel manager.
 * @return {Promise}
 */
function loadLivelabel({ loaders, assets, item, livelabelManager }, data, filteredAnimationData) {
    return new Promise((resolve, reject) => {
        const url = item.data.path;
        const object = assets.objects[item.data.objectUuid];

        if (object) {
            const success = labelData => {
                deserializeLabels({
                    object,
                    labelData: JSON.parse(labelData),
                    livelabelManager,
                    filteredAnimationData,
                    data
                });

                resolve(labelData);
            }

            loaders.file.load(url, success, undefined, reject);
        } else {
            reject("Livelabel refers to removed object");
        }
    });
}

/**
 * Deserialize labels.
 * @param {Object} data Livelabel data
 * @param {Object} livelabelManager Livelabel manager.
 */
function deserializeLabels({ labelData, object, livelabelManager, data, filteredAnimationData }) {
    if (object.uuid !== labelData.object_uuid) return;

    const labels = labelData.label;

    livelabelManager.deserialize(labelData.version, object, labels);

    labels.forEach(({ uuid }) => {
        if (uuid in filteredAnimationData) {
            const slides = data.slides;
            const restorableData = filteredAnimationData[uuid];

            Object
                .keys(restorableData)
                .forEach(slideIndex => {
                    slides[slideIndex].animation[uuid] = restorableData[slideIndex];
                });
        }
    });

    RenderManager.activeCamera.update = true;
}